import type {
  ResultCacheWindowRequest,
  ResultCacheWindowResponse,
  ResultCacheMetadataRequest,
  ResultCacheMetadataResponse,
  ResultCacheDiscardRequest,
  ResultCacheSortRequest,
  ResultCacheSortResponse,
  ResultCacheFilterRequest,
  ResultCacheFilterResponse,
  ResultCacheClearSortRequest,
  ResultCacheClearSortResponse,
  ResultCacheClearFilterRequest,
  ResultCacheClearFilterResponse,
  ResultCacheGetCellValueRequest,
  ResultCacheGetCellValueResponse,
  ResultCacheExportCsvRequest,
  ResultCacheExportCsvResponse,
  ResultCacheExportCsvProgress,
  ResultSourceProgressEvent,
} from '../../../../types/queryExecution';

/** Fetch a window of rows from the Electron result cache. */
export async function getResultCacheWindow(
  request: ResultCacheWindowRequest
): Promise<ResultCacheWindowResponse> {
  return window.electronAPI!.resultCache.getWindow(request);
}

/** Fetch metadata for a cached result set. */
export async function getResultCacheMetadata(
  request: ResultCacheMetadataRequest
): Promise<ResultCacheMetadataResponse> {
  return window.electronAPI!.resultCache.getMetadata(request);
}

/** Discard a cached result set (free memory in Electron main process). */
export async function discardResultCache(
  request: ResultCacheDiscardRequest
): Promise<void> {
  return window.electronAPI!.resultCache.discard(request);
}

/** Sort a cached result set. Returns the new total row count. */
export async function sortResultCache(
  request: ResultCacheSortRequest
): Promise<ResultCacheSortResponse> {
  return window.electronAPI!.resultCache.sort(request);
}

/** Filter a cached result set. Returns the new total row count. */
export async function filterResultCache(
  request: ResultCacheFilterRequest
): Promise<ResultCacheFilterResponse> {
  return window.electronAPI!.resultCache.filter(request);
}

/** Clear only sort on a cached result set (keeps filter). */
export async function clearResultCacheSort(
  request: ResultCacheClearSortRequest
): Promise<ResultCacheClearSortResponse> {
  return window.electronAPI!.resultCache.clearSort(request);
}

/** Clear only filter on a cached result set (keeps sort). */
export async function clearResultCacheFilter(
  request: ResultCacheClearFilterRequest
): Promise<ResultCacheClearFilterResponse> {
  return window.electronAPI!.resultCache.clearFilter(request);
}

/** Fetch the full untruncated value of a single cell from a result source. */
export async function getResultCacheCellValue(
  request: ResultCacheGetCellValueRequest
): Promise<ResultCacheGetCellValueResponse> {
  return window.electronAPI!.resultCache.getCellValue(request);
}

/** Stream-export a cached result set to CSV file. */
export async function exportCacheToCsv(
  request: ResultCacheExportCsvRequest
): Promise<ResultCacheExportCsvResponse> {
  return window.electronAPI!.resultCache.exportCsv(request);
}

/** Cancel an in-progress CSV export. */
export async function cancelCsvExport(uri: string): Promise<void> {
  return window.electronAPI!.resultCache.cancelExportCsv({ uri });
}

/** Subscribe to CSV export progress events. Returns unsubscribe function. */
export function onCsvExportProgress(
  callback: (progress: ResultCacheExportCsvProgress) => void
): () => void {
  return window.electronAPI!.resultCache.onExportCsvProgress(callback);
}

/** Subscribe to result source population progress events. Returns unsubscribe function. */
export function onSourceProgress(
  callback: (progress: ResultSourceProgressEvent) => void
): () => void {
  return window.electronAPI!.resultCache.onSourceProgress(callback);
}
